if (window.resourceBasePath) {
  // Specify the location for webpack generated files
  // This saves a few hops whilst we have hosting in AWS
  // This has to be initialised first so the application can set the appropriate resource paths
  // eslint-disable-next-line no-undef
  __webpack_public_path__ = window.resourceBasePath;
}

import { ApolloProvider } from '@apollo/client';
import { KallidusThemeVariables, PageLoader } from '@kallidus/common';
import React, { Suspense, useState } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { string } from 'prop-types';
import VersionCheck from '@ApplicationWide/VersionCheck';
import AppConfig from './AppConfig';
import checkForRedirect from './checkForRedirect';
import ErrorHandler from './ErrorHandler';
import fetchConfig, { fetchAppOnlyConfig } from './fetchConfig';
import createApolloClient from './createApolloClient';
import MainRoutes from './MainRoutes';
import MasqueradeBanner from './MasqueradeBanner';
import { ZendeskSupport } from './ZendeskSupport';
import { CompanyTrialExpired } from './CompanyTrialExpired';
import { initLanguages } from '@Utils/localisation';
import { LANGUAGE } from './language';
import { useAsync } from 'react-use';
import PreAuthConfigProvider from './auth/PreAuthConfigProvider';

checkForRedirect();

const APOLLO_CLIENT = createApolloClient(window?.baseAppConfig?.graphqlPath);

const fetchUserConfig = (setUser, preAuthConfig) =>
  fetchConfig(APOLLO_CLIENT, window.location.href, setUser, preAuthConfig);

const fetchAppConfig = () => fetchAppOnlyConfig(APOLLO_CLIENT);

const removeLoader = () => window.document.getElementById('a')?.remove();

// memoised component to be the <AppConfig /> child
// to help reduce unnecessary re-renders
// from context value updates within <AppConfig />
const AppContent = React.memo(() => (
  <div id='main-app'>
    <Suspense fallback={<PageLoader />}>
      <CompanyTrialExpired />
      <MasqueradeBanner />
      <ZendeskSupport />
      <MainRoutes />
    </Suspense>
  </div>
));

const NO_USER_CONFIRMATION = () => undefined;

const App = ({ basename }) => {
  const [brandPrimaryColor, setBrandPrimaryColor] = useState('#111111');

  const { loading } = useAsync(async () => {
    await initLanguages(LANGUAGE);
  }, []);

  if (loading) return null;

  return (
    <Router basename={basename} getUserConfirmation={NO_USER_CONFIRMATION}>
      <ApolloProvider client={APOLLO_CLIENT}>
        <KallidusThemeVariables brandPrimaryColor={brandPrimaryColor} />
        <VersionCheck />
        <ErrorHandler>
          <PreAuthConfigProvider>
            <AppConfig
              fetchAppConfig={fetchAppConfig}
              onConfigLoaded={removeLoader}
              fetchConfig={fetchUserConfig}
              onBrandColorChange={setBrandPrimaryColor}
            >
              <AppContent />
            </AppConfig>
          </PreAuthConfigProvider>
        </ErrorHandler>
      </ApolloProvider>
    </Router>
  );
};
App.propTypes = {
  basename: string,
};
export default App;
